import { EventItem } from "@/src/models/event.model";
import { useTheme } from "@/src/styles/theme-provider";
import {
  buildEventsByDate,
  toCalendarDateKey,
} from "@/src/utils/calendar-events.utils";
import { useRouter } from "expo-router";
import { ArrowLeft2, ArrowRight2 } from "iconsax-react-nativejs";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";
import { Calendar, DateData } from "react-native-calendars";
import CalendarDayCell from "./calendar-day-cell";

type EventsCalendarProps = Readonly<{
  events: EventItem[];
}>;

const NO_EVENTS: never[] = [];

const buildCalendarTheme = (monthTextColor: string, textSectionTitleColor: string) => ({
  monthTextColor,
  textSectionTitleColor,
  calendarBackground: "transparent",
});

const EventsCalendar = ({ events }: EventsCalendarProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const router = useRouter();

  const calendarTheme = useMemo(
    () =>
      buildCalendarTheme(
        themeVars["--color-onyx-default"] as string,
        themeVars["--color-ash-1"] as string,
      ),
    [themeVars],
  );

  const today = toCalendarDateKey(new Date());

  /**
   * Every day that has events, with each event's day-within-the-event.
   *
   * The day cells read straight from this instead of the library's `marking`
   * argument. `markedDates` is still handed to the Calendar so its own
   * bookkeeping is unchanged, but a custom `dayComponent` means the events a
   * cell needs are richer than a marking flag can carry, and closing over the
   * map keeps them out of a shape the library is free to reinterpret.
   */
  const eventsByDate = useMemo(() => buildEventsByDate(events), [events]);

  const markedDates = useMemo(() => {
    const marks: Record<string, { marked: boolean }> = {};
    for (const dateKey of eventsByDate.keys()) {
      marks[dateKey] = { marked: true };
    }
    return marks;
  }, [eventsByDate]);

  const handleSelectEvent = useCallback(
    (eventId: string) => router.push(`/events/${eventId}`),
    [router],
  );

  const renderArrow = (direction: "left" | "right") => (
    <View className="w-7 h-7 rounded-full items-center justify-center hover:bg-ash">
      {direction === "left" ? (
        <ArrowLeft2 size={16} color={themeVars["--color-primary-default"] as string} />
      ) : (
        <ArrowRight2 size={16} color={themeVars["--color-primary-default"] as string} />
      )}
    </View>
  );

  const renderHeader = (date: any) => {
    const parsed = new Date(date?.toString());
    const monthLabel = parsed.toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
    return (
      <View className="flex-1 items-center justify-center">
        <Text
          className="text-sm font-medium"
          style={{ color: themeVars["--color-onyx-default"] as string }}
        >
          {monthLabel}
        </Text>
      </View>
    );
  };

  const renderDay = ({
    date,
    state,
  }: {
    date?: DateData;
    marking?: any;
    state?: string;
  }) => {
    const dateKey = date?.dateString ?? "";
    return (
      <CalendarDayCell
        key={dateKey}
        dayNumber={date?.day}
        isToday={dateKey === today}
        isDisabled={state === "disabled"}
        dayEvents={eventsByDate.get(dateKey) ?? NO_EVENTS}
        onSelectEvent={handleSelectEvent}
      />
    );
  };

  return (
    <View className="rounded-2xl border border-ash-3 bg-chalk p-4">
      <Text className="text-sm font-bold text-onyx mb-3">
        {t("LABEL_CALENDAR")}
      </Text>

      <Calendar
        markingType="custom"
        markedDates={markedDates}
        theme={calendarTheme}
        dayComponent={renderDay}
        renderHeader={renderHeader}
        renderArrow={renderArrow}
      />
    </View>
  );
};

export default EventsCalendar;
